import React, { useState, useEffect } from "react";
import gsap from "gsap";
import { Chip } from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import ScrollTrigger from "gsap/ScrollTrigger";
import ScrollToPlugin from "gsap/ScrollToPlugin";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  updateQuery,
  updateQueryStartDate,
  updateQueryEndDate,
} from "../redux/querySlice";

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(ScrollToPlugin);

export default function Feed() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const queryTextRedux = useSelector((state) => state.queryText.value);
  const activityRedux = useSelector((state) => state.activity);
  const historyID = useSelector((state) => state.historicalChatID.value);
  const feedTopicsRedux = useSelector((state) => state.feed.value);
  const [feedTopics, setFeedTopics] = useState([]);

  const select = (e) => document.querySelector(e);
  const selectAll = (e) => document.querySelectorAll(e);

  const killScrollTrigger = () => {
    ScrollTrigger.killAll();
    ScrollTrigger.killAll(true);
    ScrollTrigger.getById("feed")?.kill(true);
  };

  const getSmithResponse = (text, queryDateRange) => {
    dispatch(updateQueryStartDate(queryDateRange["startTimeEpochMillis"]));
    dispatch(updateQueryEndDate(queryDateRange["endTimeEpochMillis"]));
    dispatch(updateQuery(text));
    window.scrollTo({ top: 0, behavior: "smooth" });
    killScrollTrigger();
  };

  const getDates = (queryDateRange) => {
    let startTime = new Date(queryDateRange["startTimeEpochMillis"]);
    let endTime = new Date(queryDateRange["endTimeEpochMillis"]);
    if (
      startTime.toLocaleDateString("en-US") ===
      endTime.toLocaleDateString("en-US")
    ) {
      return startTime.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
    }
    let startStr = "";
    if (startTime.getFullYear() === endTime.getFullYear()) {
      startStr += startTime.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
      });
    } else {
      startStr += startTime.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
    }
    return (
      startStr +
      " - " +
      endTime.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      })
    );
  };

  // Sync Redux with local state
  useEffect(() => {
    if (feedTopicsRedux && feedTopicsRedux.length > 0) {
      setFeedTopics(feedTopicsRedux);
    }
  }, [feedTopicsRedux]);

  useEffect(() => {
    // Change opacity of link on scroll

    if (feedTopics && feedTopics.length > 1) {
      killScrollTrigger();
      const feed = selectAll(".feed-link");
      const scrollPosition = document.getElementById("scroller").scrollTop;
      const scrollerHeight = document.getElementById("scroller").clientHeight;
      const feedContainerHeight =
        document.getElementById("feed-container").clientHeight;

      ScrollTrigger.create({
        id: "feed",
        start: 20,
        end: "max",
        onLeave: (self) => {
          self.scroll(scrollerHeight / 2 - feedContainerHeight + 40);
          ScrollTrigger.update();
        },
        onLeaveBack: (self) => {
          if (scrollPosition < scrollerHeight / 2) {
            self.scroll(0);
          } else {
            self.scroll(scrollerHeight / 2);
          }
          ScrollTrigger.update();
        },
      });

      feed.forEach((item) => {
        gsap.to(item, {
          keyframes: {
            scale: [0.4, 0.7, 1, 0.8, 0.4],
            opacity: [0.1, 0.6, 1, 0.6, 0.1],
          },
          transformOrigin: "left top",
          repeat: 0,
          yoyo: true,
          ease: "none",
          scrollTrigger: {
            // scroller: container,
            trigger: item,
            start: "center bottom",
            end: "center top",
            // markers:true,
            scrub: true,
          },
        });
      });

      // console.log("container height:", document.getElementById('feed')?.clientHeight)

      gsap.from("#scroller", { duration: 1, opacity: 1 });

      let moveDirection = "max";

      // pause on hover, play on mouseLeave
      let scrollerBox = document.getElementById("scroller");
      scrollerBox.addEventListener(
        "mouseleave",
        () => autoScroll.play(),
        false
      );
      scrollerBox.addEventListener(
        "mouseover",
        () => autoScroll.pause(),
        false
      );
      // scrollerBox.addEventListener("wheel", function (event) {
      //   autoScroll.pause()
      // }, false);

      let autoScroll = gsap.to(window, {
        duration: scrollerHeight / 50,
        scrollTo: { y: moveDirection, autoKill: true },
        ease: "none",
        delay: 0,
      });

      ScrollTrigger.observe({
        type: "wheel,scroll,pointer",
        onWheel: (self) => {
          autoScroll.kill();
        },
        // onHover: (self) => {
        //   autoScroll.pause()
        // },
        // onHoverEnd: (self) => {
        //   autoScroll.play();
        // },
        // onHoverEnd: (self) => {
        //   autoScroll = gsap.to(window, {
        //     duration: scrollerHeight/80,
        //     scrollTo: { y: moveDirection }
        //   });
        // },
        // onStop: (self) => {
        //   autoScroll = gsap.to(window, {
        //     duration: scrollerHeight/80,
        //     scrollTo: { y: moveDirection, autoKill: false }
        //   });
        // ScrollTrigger.update();
        // },
        onStopDelay: 0.5,
      });
    }
  }, [feedTopics]);

  useEffect(() => {
    killScrollTrigger();
  }, [activityRedux, queryTextRedux]);

  return (
    <div id="feedBox">
      <div id="feed-container">
        <ul id="scroller" className="infinite-scroll-list">
          {feedTopics &&
            feedTopics.length > 1 &&
            feedTopics.map((item) => (
              <li
                className="feed-link"
                key={item.display_text}
                onClick={() =>
                  getSmithResponse(item.display_text, item.queryDateRange)
                }
              >
                <img src={item.thumbnail_url} />
                <div id="feed-text">
                  {item.display_text || item.query}
                  <br />
                  <EventIcon
                    sx={{ fontSize: 20, mr: "6px", color: "#666666" }}
                  />
                  <Chip
                    variant="outlined"
                    sx={{
                      border: 0,
                      marginRight: 0,
                      justifyContent: "left",
                      letterSpacing: "-0.5px",
                      "& .MuiChip-label": {
                        textAlign: "left",
                        padding: 0,
                        opacity: 1,
                        textTransform: "uppercase",
                        fontFamily: "Roboto Condensed",
                        marginTop: "1px",
                      },
                    }}
                    label={getDates(item.queryDateRange)}
                  />
                </div>
              </li>
            ))}
          {feedTopics &&
            feedTopics.length > 1 &&
            feedTopics.map((item) => (
              <li
                className="feed-link"
                key={item.display_text}
                onClick={() =>
                  getSmithResponse(item.display_text, item.queryDateRange)
                }
              >
                <img src={item.thumbnail_url} />
                <div id="feed-text">
                  {item.display_text || item.query}
                  <br />
                  <EventIcon
                    sx={{ fontSize: 20, mr: "6px", color: "#666666" }}
                  />
                  <Chip
                    variant="outlined"
                    sx={{
                      border: 0,
                      marginRight: 0,
                      justifyContent: "left",
                      letterSpacing: "-0.5px",
                      "& .MuiChip-label": {
                        textAlign: "left",
                        padding: 0,
                        opacity: 1,
                        textTransform: "uppercase",
                        fontFamily: "Roboto Condensed",
                        marginTop: "1px",
                      },
                    }}
                    label={getDates(item.queryDateRange)}
                  />
                </div>
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
}
